import React, { useCallback, useEffect, useState } from 'react';
import { ExerVisionProvider } from '@exerai/vision-engine-web';
import IosShareIcon from '@mui/icons-material/IosShare';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { addYears } from 'date-fns';
import { DateTime } from 'luxon';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { ArtifactType, Paths, Products } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { useMuiUtils } from '@/common/hooks/MuiUtils';
import { toasts } from '@/common/toasts';
import { AdminScanSessionPagedRequestParams, Maybe, OrganizationLocation, ScanSessionAdminRow } from '@/common/types';
import { getAssessmentIdDashName, getUrlSafeAssessmentName, isHandAssessment } from '@/common/utils';
import { SessionReplayContainer } from '@/components/Scan/Replay/SessionReplayContainer';
import { DateRangeActions } from '@/components/common/DateRangeActions/DateRangeActions';
import { CalendarIcon } from '@/components/common/Icons/CalendarIcon';
import { EditIcon } from '@/components/common/Icons/EditIcon';
import { SessionReplayAction } from '@/components/common/Pose/SessionReplayAction';
import { AssessmentFilter } from '@/components/common/Table/Filters/AssessmentFilter';
import { LocationMultiFilter } from '@/components/common/Table/Filters/LocationMultiFilter';
import { PaginationContainer } from '@/components/common/Table/PaginationContainer';
import { TableSearchFilter } from '@/components/common/Table/TableSearchFilter/TableSearchFilter';
import { CopyButton, CopyTextWrapper } from '@/components/common/Table/style';
import { TableActionsWrapper } from '@/components/common/Table/styles';
import { useAssessmentsContext } from '@/components/context/hooks/AssessmentsContext';
import { usePaginationContext } from '@/components/context/hooks/PaginationContext';
import { DateRangeState } from '@/recoil/atoms/dateRangeAtom';
import { scanFilterState } from '@/recoil/atoms/scanFilterAtom';
import { resultIsError } from '@/services/HttpService';
import { HandsSessionReplayContainer } from '../Replay/HandsSessionReplayContainer';
import { AdminOutcomesActionContainer } from './AdminOutcomesActionContainer';
import { AdminScanTable } from './AdminScanTable';
import { RelocateSessionModal } from './RelocateSessionModal';
import { ScanDetailsAction } from './ScanDetailsAction';
import { ScanSessionCascadingLogAction } from './ScanSessionCascadingLogAction';
import { ScanSessionReportAction } from './ScanSessionReportAction';
import { ScanUUIDDisplay } from './ScanUUIDDisplay';
import { ListControlsContainer } from './styles';

export const AdminScanTableContainer = () => {
    const { httpService } = useHttpContext();
    const { errorToast, successToast } = toasts;
    const { palette } = useMuiUtils();
    const [rows, setRows] = useState<ScanSessionAdminRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { assessmentsDict } = useAssessmentsContext();
    const { page, setPaginationMeta, filters } = usePaginationContext();
    const scanFilters = useRecoilValue(scanFilterState);
    const dateRange = useRecoilValue(DateRangeState);
    const [organizationLocations, setOrganizationLocations] = useState<OrganizationLocation[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [uuidFilter, setUuidFilter] = useState<Maybe<string>>(searchParams.get('sessionUUID') ?? undefined);

    const handleUuidFilterChange = useCallback(
        (value: Maybe<string>) => {
            setUuidFilter(value);
            setSearchParams(value ? { sessionUUID: value } : {}, { replace: true });
        },
        [setSearchParams],
    );
    const [providerFilter, setProviderFilter] = useState<Maybe<string>>(undefined);
    const [participantFilter, setParticipantFilter] = useState<Maybe<string>>(undefined);
    const [sessionToRelocate, setSessionToRelocate] = useState<Maybe<ScanSessionAdminRow>>(undefined);

    useEffect(() => {
        (async () => {
            const res = await httpService.getAdminScanOrganizationLocations();
            if (!res || resultIsError(res)) {
                errorToast(`Could not load locations for scan organizations. ${res?.message || ''}`);
            } else {
                setOrganizationLocations(res);
            }
        })();
    }, []);

    const getRequestParams = useCallback((): Maybe<AdminScanSessionPagedRequestParams> => {
        const params = {} as AdminScanSessionPagedRequestParams;
        params.page = page;
        params.limit = 15;
        if (uuidFilter) {
            params.sessionUUID = uuidFilter;
        } else {
            if (scanFilters.assessmentIds.length > 0) {
                params.assessmentIds = scanFilters.assessmentIds;
            }

            if (scanFilters.locationIds.length > 0) {
                params.locationIds = scanFilters.locationIds;
            }

            if (providerFilter) {
                params.provider = providerFilter;
            }
            if (participantFilter) {
                params.participant = participantFilter;
            }
            params.start = dateRange.startDate.toISOString();
            params.end = dateRange.endDate.toISOString();
        }

        return params;
    }, [page, filters, scanFilters, uuidFilter, providerFilter, dateRange, participantFilter]);

    const getSessions = useCallback(
        async (assessmentsDict) => {
            setIsLoading(true);
            const params = getRequestParams();
            if (!params) {
                setIsLoading(false);
                return;
            }

            const res = await httpService.adminGetScanSessions(params);

            if (!res || resultIsError(res)) {
                errorToast(`Could not load scan sessions. ${res?.message || ''}`);
            } else {
                const resultRows = res.items.map(
                    ({
                        id,
                        createdDate,
                        updatedDate,
                        sessionUUID,
                        session,
                        location,
                        practitioner,
                        organization,
                        artifacts,
                        participant,
                        resultsInsights,
                    }) => {
                        const assessmentName = getAssessmentIdDashName(session?.data.assessmentId, assessmentsDict);
                        return {
                            id,
                            createdDate,
                            updatedDate,
                            sessionUUID,
                            session,
                            locationName: location?.name || '',
                            locationId: location?.id,
                            practitionerName: practitioner ? `${practitioner.firstName} ${practitioner.lastName}` : '',
                            assessmentName,
                            organizationName: organization?.name || '',
                            organizationId: organization?.id,
                            organizationImageUrl: organization?.logoUrl,
                            assessmentId: session?.data.assessmentId,
                            action: {
                                sessionUUID,
                                artifacts,
                                sessionIdentifier: `${DateTime.fromISO(createdDate).toFormat(
                                    'MM-dd-yyyy',
                                    // eslint-disable-next-line prettier/prettier
                                )}${getUrlSafeAssessmentName(session?.data.assessmentId, assessmentsDict)}${
                                    (participant && `_${participant.uid}`) || ''
                                    // eslint-disable-next-line prettier/prettier
                                }`,
                                resultsInsights,
                            },
                            participantDetails: {
                                uid: (participant && participant.uid) || '',
                                archivedDate:
                                    (participant && participant.isArchived && participant.archivedDate) || null,
                            },
                            sessionSpeed: session?.data.results.find((d) => d.name === 'Avg Speed')?.value,
                        };
                    },
                );
                setRows(resultRows);
                setPaginationMeta(res.metadata);
            }
            setIsLoading(false);
        },
        [httpService, setPaginationMeta, getRequestParams],
    );
    useEffect(() => {
        (async () => {
            if (assessmentsDict) {
                await getSessions(assessmentsDict);
            }
        })();
    }, [getSessions, assessmentsDict]);

    const columnStructure = [
        {
            Header: 'Organization',
            accessor: 'organizationName',
            width: '10%',
            Cell: ({ value }) => <>{value}</>,
        },
        {
            Header: 'Location',
            accessor: 'locationName',
            width: '10%',
            Cell: ({ row, value }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{value}</span>
                    <Tooltip title="Relocate session">
                        <IconButton
                            size="small"
                            sx={{ p: 0.375 }}
                            onClick={() => setSessionToRelocate(row.original as ScanSessionAdminRow)}
                        >
                            <EditIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        {
            Header: 'Assessment',
            accessor: 'assessmentName',
            width: '15%',
            Cell: ({ value }) => <>{value}</>,
        },
        {
            Header: 'Provider',
            accessor: 'practitionerName',
            width: '10%',
            Cell: ({ value }) => <>{value}</>,
        },
        {
            Header: 'Created',
            accessor: 'createdDate',
            width: '12%',
            Cell: ({ value }) => (
                <>
                    {value ? (
                        <>
                            {DateTime.fromISO(value).toLocaleString(DateTime.DATE_SHORT)}{' '}
                            {DateTime.fromISO(value).toLocaleString(DateTime.TIME_WITH_SECONDS)}
                        </>
                    ) : (
                        ''
                    )}
                </>
            ),
        },
        {
            Header: 'UUID',
            accessor: 'sessionUUID',
            width: '10%',
            Cell: ({ value }) => <ScanUUIDDisplay uuid={value} />,
        },
        {
            Header: 'UID',
            accessor: 'participantDetails',
            width: '10%',
            Cell: ({ value }) => (
                <>
                    <Typography>{value.uid}</Typography>
                    {value.archivedDate && (
                        <Typography variant="subtitle2">
                            <Tooltip
                                title={`Archived on: ${DateTime.fromISO(value.archivedDate).toLocaleString(
                                    DateTime.DATETIME_SHORT_WITH_SECONDS,
                                )}`}
                            >
                                <Box style={{ display: 'flex', alignItems: 'center' }}>
                                    Archived
                                    <Box style={{ color: palette.info[500], display: 'flex', marginLeft: 4 }}>
                                        <CalendarIcon />
                                    </Box>
                                </Box>
                            </Tooltip>
                        </Typography>
                    )}
                </>
            ),
        },
        {
            Header: 'Action',
            accessor: 'action',
            width: '40%',
            Cell: ({ row, value: { sessionUUID, artifacts, sessionIdentifier, resultsInsights } }) => {
                const hasReplayableArtifacts =
                    artifacts.includes(ArtifactType.PoseLog) ||
                    artifacts.includes(ArtifactType.HandsLog) ||
                    artifacts.includes(ArtifactType.ArCapturePackage) ||
                    artifacts.includes(ArtifactType.BodyHAndArCapturePackage);

                return (
                    <TableActionsWrapper style={{ justifyContent: 'flex-start' }}>
                        <SessionReplayAction product={Products.SCAN} disabled={!hasReplayableArtifacts}>
                            {artifacts.includes(ArtifactType.ArCapturePackage) ? (
                                <> AR Capture Log playback not supported</>
                            ) : isHandAssessment(row.original.assessmentId) ? (
                                <ExerVisionProvider minDrawingConfidence={0.4}>
                                    <HandsSessionReplayContainer
                                        sessionUUID={sessionUUID}
                                        assessmentId={row.original.assessmentId}
                                    />
                                </ExerVisionProvider>
                            ) : (
                                <ExerVisionProvider>
                                    <SessionReplayContainer
                                        client="scan"
                                        sessionUUID={sessionUUID}
                                        assessmentId={row.original.assessmentId}
                                        resultsInsights={resultsInsights}
                                    />
                                </ExerVisionProvider>
                            )}
                        </SessionReplayAction>
                        <AdminOutcomesActionContainer
                            org={{ id: row.original.organizationId, name: row.original.organizationName }}
                            scanSessionOutcomeData={row.original}
                            sessionIdentifier={sessionIdentifier}
                        />
                        <ScanSessionCascadingLogAction sessionUUID={sessionUUID} artifacts={artifacts} />
                        {process.env.ENVIRONMENT !== 'PRODUCTION' && artifacts.includes(ArtifactType.SessionReport) && (
                            <ScanSessionReportAction sessionUUID={sessionUUID} />
                        )}
                        <ScanDetailsAction row={row.original as ScanSessionAdminRow} />
                        <Tooltip title="Copy link">
                            <CopyButton size="small" onClick={() => successToast('Link copied.')}>
                                <CopyToClipboard
                                    text={`${window.location.origin}${Paths.adminScanSessions}?sessionUUID=${sessionUUID}`}
                                >
                                    <CopyTextWrapper>
                                        <IosShareIcon fontSize="small" />
                                    </CopyTextWrapper>
                                </CopyToClipboard>
                            </CopyButton>
                        </Tooltip>
                    </TableActionsWrapper>
                );
            },
        },
    ];

    return (
        <>
            <ListControlsContainer className="scan-filter-row">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flex: '1 1 150px',
                        '& > div': { width: '100%' },
                    }}
                >
                    <TableSearchFilter
                        filter={participantFilter}
                        setFilter={setParticipantFilter}
                        searchLabel="Participant"
                        disabled={!!uuidFilter}
                    />
                    <TableSearchFilter
                        filter={providerFilter}
                        setFilter={setProviderFilter}
                        searchLabel="Provider"
                        disabled={!!uuidFilter}
                    />
                </Box>
                <LocationMultiFilter organizationLocations={organizationLocations} disabled={!!uuidFilter} />
                <AssessmentFilter disabled={!!uuidFilter} />
                <DateRangeActions
                    disabled={!!uuidFilter}
                    buttonStyle={{ backgroundColor: 'white', borderColor: 'rgb(194, 196, 204)' }}
                    defaultState={{
                        startDate: addYears(new Date(), -2),
                        endDate: new Date(),
                        rangeRadioOptions: { seven: false, thirty: false, ninety: false },
                    }}
                />
            </ListControlsContainer>
            <Divider>or</Divider>
            <ListControlsContainer sx={{ '& > div': { width: '100%', minWidth: 0 } }}>
                <TableSearchFilter
                    filter={uuidFilter}
                    setFilter={handleUuidFilterChange}
                    searchLabel="Session UUID"
                    disabled={
                        !!participantFilter ||
                        !!providerFilter ||
                        scanFilters.assessmentIds.length > 0 ||
                        scanFilters.locationIds.length > 0
                    }
                />
            </ListControlsContainer>
            <AdminScanTable columnStructure={columnStructure} data={rows} isLoading={isLoading} />
            <PaginationContainer />
            {sessionToRelocate && (
                <RelocateSessionModal
                    session={sessionToRelocate}
                    organizationLocations={organizationLocations}
                    onClose={() => setSessionToRelocate(undefined)}
                    onSuccess={async () => {
                        setSessionToRelocate(undefined);
                        await getSessions(assessmentsDict);
                    }}
                />
            )}
        </>
    );
};
